import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  RateReview as RateReviewIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import projectsService from '../services/projectsService';
import api from '../services/authService';
import { WorkPackage, WorkUnit, RevisionName, Party, CreateDocumentRevisionForm, MDRData, Project, WorkflowStep, Discipline, DocumentType, User } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import { hasStartedRevision, WORKFLOW_ASSIGNMENT_CONFLICT_ERROR } from '../utils/revisionUtils';
import UnifiedDocumentDialog from '../components/UnifiedDocumentDialog';
import TransmittalsTable from '../components/TransmittalsTable';
import MDRTableAgGrid from '../components/MDRTableAgGrid';
import ProjectCompletionFeedbackDialog from '../components/ProjectCompletionFeedbackDialog';
import ProjectCompletionFeedbackPanel from '../components/ProjectCompletionFeedbackPanel';
import CompletionLetterDialog from '../components/CompletionLetterDialog';
import DownloadSelectionDialog, { DownloadableFileItem } from '../components/DownloadSelectionDialog';
import { openFilePreviewInNewTab, triggerBlobDownload } from '../utils/filePreview';
import { ProjectTemplate } from '../services/projectsService';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, hasCapabilityOrProjectPm, CAP } from '../utils/permissions';
import JSZip from 'jszip';
import cfosService from '../services/cfosService';
import CfosWorkspace from '../components/cfos/CfosWorkspace';
import type { User as CfosUser } from '../lib/cfos/types';
import { useTabSearchParam } from '../hooks/useTabSearchParam';

type DocumentsTab = 'documents' | 'cfos' | 'transmittals' | 'feedback';

// Nautilus Console Design System Colors
const NAUTILUS = {
  deepOcean: '#0f2134',
  sand: '#ccba9b',
  abyssal: '#0a1622',
};

const DocumentsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();

  // Design System Tokens
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [documents, setDocuments] = useState<MDRData[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<MDRData | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  // Filter states
  const [filters, setFilters] = useState({
    nd_number: '',
    title: '',
    work_unit: '',
    work_package: '',
    document_type: '',
  });

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadForm, setBulkUploadForm] = useState({
    template_file: null as File | null,
  });
  const [projectUsers, setProjectUsers] = useState<User[]>([]);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [workUnits, setWorkUnits] = useState<WorkUnit[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState<{
    message: string;
    results: Array<{ nd_number: string; title: string; document_id: number; status: string }>;
    errors: string[];
  } | null>(null);

  // Revision creation states
  const [revisionNames, setRevisionNames] = useState<RevisionName[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [completionLetterDialogOpen, setCompletionLetterDialogOpen] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [projectTemplates, setProjectTemplates] = useState<ProjectTemplate[]>([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [downloadingAllTemplates, setDownloadingAllTemplates] = useState(false);

  // Approved revision version files download dialog
  const [revisionDownloadDialogOpen, setRevisionDownloadDialogOpen] = useState(false);
  const [revisionDownloadFiles, setRevisionDownloadFiles] = useState<DownloadableFileItem[]>([]);
  const [revisionDownloadLoading, setRevisionDownloadLoading] = useState(false);
  const [revisionDownloadContext, setRevisionDownloadContext] = useState<{ docId: number; revisionId: number; versionId: number } | null>(null);
  const [revisionZipDownloading, setRevisionZipDownloading] = useState(false);

  // Resources Tab State
  const [revisionFiles, setRevisionFiles] = useState<Record<string, any[]>>({});
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const tablePanelRef = useRef<HTMLDivElement | null>(null);
  const canAccessCfos = hasCapability(user, CAP.CFOS);
  const [cfosUser, setCfosUser] = useState<CfosUser | null>(null);
  const [cfosSandboxId, setCfosSandboxId] = useState<string | null>(null);
  const [cfosLoadError, setCfosLoadError] = useState('');
  const cfosUserRef = useRef<CfosUser | null>(null);

  const allowedTabs = useMemo(() => {
    const tabs: DocumentsTab[] = ['documents'];
    if (canAccessCfos) tabs.push('cfos');
    tabs.push('transmittals');
    if (feedbackCount > 0) tabs.push('feedback');
    return tabs;
  }, [canAccessCfos, feedbackCount]);

  const [activeTab] = useTabSearchParam(allowedTabs, 'documents');

  const setActiveTab = useCallback(
    (tab: DocumentsTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          if (tab !== 'cfos') {
            next.delete('view');
            next.delete('folder');
            next.delete('file');
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: DocumentsTab) => {
    setActiveTab(newValue);
  };

  // Drop orphan CFOS nested params when not on the CFOS tab (e.g. bookmarked URL).
  useEffect(() => {
    if (activeTab === 'cfos') return;
    if (!searchParams.has('view') && !searchParams.has('folder') && !searchParams.has('file')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        next.delete('folder');
        next.delete('file');
        return next;
      },
      { replace: true },
    );
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (!canAccessCfos || !projectId || activeTab !== 'cfos') return;
    let cancelled = false;
    setCfosLoadError('');
    (async () => {
      try {
        let me = cfosUserRef.current;
        if (!me) {
          me = await cfosService.me();
          if (cancelled) return;
          cfosUserRef.current = me;
          setCfosUser(me);
        }
        const sandbox = await cfosService.getOrCreateSandbox(Number(projectId));
        if (cancelled) return;
        setCfosSandboxId(sandbox.id);
      } catch (err: any) {
        if (!cancelled) setCfosLoadError(err.message || 'Failed to load CFOS');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, canAccessCfos, projectId]);

  const handleExpandRevision = useCallback(async (docId: number, revId: number) => {
    const key = `${docId}-${revId}`;
    if (revisionFiles[key]) return; // Already loaded

    try {
      // 1. Get workflow to find latest version ID
      const workflow = await projectsService.getRevisionWorkflow(Number(projectId), docId, revId);

      if (workflow.latest_version) {
        // 2. Get files for that version
        const filesData = await projectsService.getRevisionVersionFiles(
          Number(projectId),
          docId,
          revId,
          workflow.latest_version.version_id
        );

        setRevisionFiles(prev => ({
          ...prev,
          [key]: filesData.files || []
        }));
      } else {
        // No version, so no files
        setRevisionFiles(prev => ({
          ...prev,
          [key]: []
        }));
      }
    } catch (err) {
      console.error(`Failed to load files for revision ${revId}`, err);
      // Mark as loaded but empty to prevent infinite referencing
      setRevisionFiles(prev => ({
        ...prev,
        [key]: []
      }));
    }
  }, [projectId, revisionFiles]);

  const handleDownloadFile = useCallback(async (docId: number, revId: number, versionId: number, fileId: number, filename: string) => {
    if (!projectId) return;
    try {
      await projectsService.downloadRevisionFile(Number(projectId), docId, revId, versionId, fileId, filename);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  }, [projectId]);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const [documentsData, projectsData] = await Promise.all([
        projectsService.getMDRByProject(Number(projectId)),
        projectsService.getProjects()
      ]);
      setDocuments(documentsData);
      setProjects(projectsData);
      setError(null);
    } catch (err) {
      setError('Failed to load documents');
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [projectId]);

  const loadUserInfo = useCallback(async () => {
    try {
      const response = await api.get('/auth/profile');
      setUserRoles(response.data.user.roles || []);

      if (projectId) {
        const usersData = await projectsService.getProjectUsers(Number(projectId));
        setProjectUsers(usersData);
      }
    } catch (err) {
      console.error('Error loading user info:', err);
    }
  }, [projectId]);

  const loadProjectDetails = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await projectsService.getProjectDetails(Number(projectId));
      setCurrentProject(response.project);
    } catch (err) {
      console.error('Error loading project details:', err);
    }
  }, [projectId]);

  const loadProjectTemplates = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await projectsService.getProjectTemplates(Number(projectId));
      setProjectTemplates(data.templates || []);
    } catch (err) {
      console.error('Error loading project templates:', err);
      setProjectTemplates([]);
    }
  }, [projectId]);

  const loadRevisionData = useCallback(async () => {
    try {
      const [revisionNamesData, partiesData] = await Promise.all([
        projectsService.getRevisionNames(),
        projectsService.getProjectParties(Number(projectId)),
      ]);
      setRevisionNames(revisionNamesData);
      setParties(partiesData);
    } catch (err) {
      console.error('Error loading revision data:', err);
    }
  }, [projectId]);

  const loadDropdownData = useCallback(async (selectedWorkPackageId?: string) => {
    try {
      const [workPackagesData, workUnitsData, documentTypesData, disciplinesData] = await Promise.all([
        projectsService.getWorkPackages(Number(projectId)),
        projectsService.getWorkUnits(Number(projectId), selectedWorkPackageId ? Number(selectedWorkPackageId) : undefined),
        projectsService.getDocumentTypes(),
        projectsService.getDisciplines(),
      ]);
      setWorkPackages(workPackagesData);
      setWorkUnits(workUnitsData);
      setDocumentTypes(documentTypesData);
      setDisciplines(disciplinesData);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  }, [projectId]);

  const handleBulkUpload = async () => {
    if (!bulkUploadForm.template_file || !projectId) return;

    try {
      setBulkUploading(true);
      const results = await projectsService.bulkUploadDocuments(
        Number(projectId),
        bulkUploadForm.template_file
      );

      setBulkUploadResults(results);
      setBulkUploadForm({ template_file: null });
      loadDocuments();
      setError(null);
    } catch (err) {
      setError('Failed to bulk upload documents');
      console.error('Error bulk uploading documents:', err);
    } finally {
      setBulkUploading(false);
    }
  };

  const templateDownloadItems: DownloadableFileItem[] = projectTemplates.map((template, index) => ({
    id: index,
    templateId: template.id,
    filename: template.filename,
    category: 'template',
    size: template.size,
    mime_type: template.mime_type,
    has_path: true,
    processing_status: 'ready',
  }));

  const handleViewTemplate = async (file: DownloadableFileItem) => {
    if (!projectId || !file.templateId) return;
    try {
      const blob = await projectsService.getProjectTemplateBlob(Number(projectId), file.templateId);
      openFilePreviewInNewTab(blob, file.filename, file.mime_type);
    } catch (err) {
      setError('Failed to open template');
      console.error('Error opening template:', err);
    }
  };

  const handleDownloadTemplate = async (file: DownloadableFileItem) => {
    if (!projectId || !file.templateId) return;
    try {
      const blob = await projectsService.getProjectTemplateBlob(Number(projectId), file.templateId);
      triggerBlobDownload(blob, file.filename, file.mime_type);
    } catch (err) {
      setError('Failed to download template');
      console.error('Error downloading template:', err);
    }
  };

  const handleDownloadAllTemplates = async (selectedFiles: DownloadableFileItem[]) => {
    if (!projectId || selectedFiles.length === 0) return;
    try {
      setDownloadingAllTemplates(true);
      if (selectedFiles.length === templateDownloadItems.length) {
        await projectsService.downloadProjectTemplatesZip(Number(projectId));
        return;
      }

      const zip = new JSZip();
      await Promise.all(
        selectedFiles.map(async (file) => {
          if (!file.templateId) return;
          const blob = await projectsService.getProjectTemplateBlob(Number(projectId), file.templateId);
          zip.file(file.filename, await blob.arrayBuffer());
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = window.document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', 'project_templates.zip');
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(zipUrl);
    } catch (err) {
      setError('Failed to download templates');
      console.error('Error downloading templates zip:', err);
    } finally {
      setDownloadingAllTemplates(false);
    }
  };

  const handleDownloadLatest = async (docId: number, revisionId: number | undefined, lastRevision: string) => {
    if (!projectId) return;

    // Fallback to legacy direct download if we don't have a revision id.
    if (!revisionId) {
      try {
        await projectsService.downloadDocumentLatestRevision(Number(projectId), docId, lastRevision);
      } catch (err) {
        setError('Failed to download document');
        console.error('Error downloading document:', err);
      }
      return;
    }

    setRevisionDownloadContext(null);
    setRevisionDownloadFiles([]);
    setRevisionDownloadLoading(true);
    setRevisionDownloadDialogOpen(true);
    try {
      const workflow = await projectsService.getRevisionWorkflow(Number(projectId), docId, revisionId);
      const versionId = workflow.latest_version?.version_id;
      if (!versionId) {
        setRevisionDownloadFiles([]);
        return;
      }
      setRevisionDownloadContext({ docId, revisionId, versionId });
      const result = await projectsService.getRevisionVersionFiles(Number(projectId), docId, revisionId, versionId);
      const files: DownloadableFileItem[] = (result.files || []).map((f: { id: number; filename: string; category: string; size?: number; mime_type?: string; has_path?: boolean; processing_status?: string }) => ({
        id: f.id,
        filename: f.filename,
        category: f.category,
        size: f.size ?? 0,
        mime_type: f.mime_type,
        has_path: f.has_path,
        processing_status: f.processing_status,
        source: 'version' as const,
      }));
      setRevisionDownloadFiles(files);
    } catch (err) {
      setError('Failed to load files for this version');
      console.error('Error loading version files:', err);
    } finally {
      setRevisionDownloadLoading(false);
    }
  };

  const handleViewRevisionVersionFile = async (file: DownloadableFileItem) => {
    if (!projectId || !revisionDownloadContext) return;
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('PDF is still being stamped and is not ready for download.');
      return;
    }
    try {
      const blob = await projectsService.getRevisionFileBlob(
        Number(projectId),
        revisionDownloadContext.docId,
        revisionDownloadContext.revisionId,
        revisionDownloadContext.versionId,
        file.id
      );
      openFilePreviewInNewTab(blob, file.filename, file.mime_type);
    } catch (err) {
      setError('Failed to open file');
      console.error('Error viewing file:', err);
    }
  };

  const handleDownloadRevisionVersionFile = async (file: DownloadableFileItem) => {
    if (!projectId || !revisionDownloadContext) return;
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('PDF is still being stamped and is not ready for download.');
      return;
    }
    try {
      const blob = await projectsService.getRevisionFileBlob(
        Number(projectId),
        revisionDownloadContext.docId,
        revisionDownloadContext.revisionId,
        revisionDownloadContext.versionId,
        file.id
      );
      triggerBlobDownload(blob, file.filename, file.mime_type);
    } catch (err) {
      setError('Failed to download file');
      console.error('Error downloading file:', err);
    }
  };

  const handleDownloadRevisionVersionFilesAsZip = async (selectedFiles: DownloadableFileItem[]) => {
    if (!projectId || !revisionDownloadContext || selectedFiles.length === 0 || revisionZipDownloading) return;
    setRevisionZipDownloading(true);
    try {
      const zip = new JSZip();
      const fileBlobs = await Promise.all(
        selectedFiles.map(async (file) => {
          const blob = await projectsService.getRevisionFileBlob(
            Number(projectId),
            revisionDownloadContext.docId,
            revisionDownloadContext.revisionId,
            revisionDownloadContext.versionId,
            file.id
          );
          return { filename: file.filename, blob };
        })
      );
      await Promise.all(
        fileBlobs.map(async ({ filename, blob }) => {
          zip.file(filename, await blob.arrayBuffer());
        })
      );
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = window.document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', 'version_files.zip');
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(zipUrl);
    } catch (err) {
      setError('Failed to create ZIP download');
      console.error('Error creating zip download:', err);
    } finally {
      setRevisionZipDownloading(false);
    }
  };

  const canEditDocument = hasCapability(user, CAP.UPDATE_DOCUMENT);
  const canCreateDocuments = hasCapabilityOrProjectPm(
    user,
    CAP.CREATE_DOCUMENT,
    currentProject?.pm_id,
  );
  const isProjectClosed = (currentProject?.status || '').toLowerCase() === 'closed';
  const isSelectedDocumentMetadataLocked = selectedDocument?.metadata_edit_locked === true;
  const canEditSelectedDocument = canEditDocument && !isSelectedDocumentMetadataLocked;
  const showDocumentCreateOrEditButton =
    (selectedDocument && canEditSelectedDocument) || canCreateDocuments;
  const canCancelSelectedDocument = Boolean(
    selectedDocument &&
    hasCapabilityOrProjectPm(user, CAP.CANCEL_DOCUMENT, currentProject?.pm_id) &&
    !selectedDocument.is_cancelled
  );

  const handleSmartDocumentButtonClick = () => {
    if (isProjectClosed) {
      setError('This project is closed. Document creation/editing is locked.');
      return;
    }
    if (selectedDocument && canEditDocument && isSelectedDocumentMetadataLocked) {
      setError('Document metadata is locked because the author has already uploaded a version.');
      return;
    }
    if (!(selectedDocument && canEditSelectedDocument) && !canCreateDocuments) {
      setError('You do not have permission to create documents on this project.');
      return;
    }
    setDocumentDialogOpen(true);
  };

  const handleCancelDocument = async () => {
    if (!projectId || !selectedDocument || !canCancelSelectedDocument) return;
    const ok = window.confirm(
      'Cancel this document? This removes workflow from all revisions and watermarks all main documents as CANCELLED.'
    );
    if (!ok) return;
    try {
      setLoading(true);
      await projectsService.cancelDocument(Number(projectId), selectedDocument.document_id);
      await loadDocuments();
      setSelectedDocument(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel document');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedbackCount = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await projectsService.listCompletionFeedback(Number(projectId), 1);
      setFeedbackCount(data.items.length);
    } catch {
      setFeedbackCount(0);
    }
  }, [projectId]);

  // Debounce filters
  useEffect(() => {
    if (projectId) {
      loadDocuments();
      loadDropdownData();
      loadRevisionData();
      loadUserInfo();
      loadProjectDetails();
      loadProjectTemplates();
    }
  }, [projectId, loadDocuments, loadDropdownData, loadRevisionData, loadUserInfo, loadProjectDetails, loadProjectTemplates]);

  useEffect(() => {
    if (isProjectClosed && projectId) {
      loadFeedbackCount();
    }
  }, [isProjectClosed, projectId, loadFeedbackCount]);

  useEffect(() => {
    const handleOutsideTableClick = (event: MouseEvent) => {
      if (documentDialogOpen) {
        return;
      }

      const target = event.target as Node | null;
      if (!target) return;

      if (tablePanelRef.current?.contains(target)) return;
      if (toolbarRef.current?.contains(target)) return;

      setSelectedDocument((prev) => (prev ? null : prev));
    };

    document.addEventListener('mousedown', handleOutsideTableClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideTableClick);
    };
  }, [documentDialogOpen]);

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  const pageFillSx = {
    height: `calc(100vh - ${theme.spacing(10)} - ${theme.spacing(4)})`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  } as const;

  const tabPanelSx = (isActive: boolean) => ({
    flex: isActive ? 1 : 'none',
    minHeight: 0,
    width: '100%',
    display: isActive ? 'flex' : 'none',
    flexDirection: 'column' as const,
  });

  return (
    <Box sx={pageFillSx}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2, flexShrink: 0 }}
        aria-label="breadcrumb"
      >
        <MuiLink component={Link} to="/projects" underline="hover" color="inherit">
          Projects
        </MuiLink>
        <Typography color="text.primary">Documents</Typography>
      </Breadcrumbs>

      <Box ref={toolbarRef} display="flex" alignItems="center" justifyContent="space-between" mb={1.5} sx={{ flexShrink: 0 }}>
        <Box display="flex" alignItems="center">
          <DescriptionIcon sx={{ mr: 2, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, fontSize: '2rem' }} />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              Project Documents
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: isDark ? '#b0bec5' : '#546e7a',
                fontFamily: 'monospace',
                letterSpacing: '0.03em',
              }}
            >
              {currentProject?.nd_number
                ? `${currentProject.nd_number} - ${currentProject.name}`
                : currentProject?.name || `Project ${projectId}`}
            </Typography>
          </Box>
          {hasCapability(user, CAP.UPDATE_PROJECT) && (
            <Button
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate(`/projects/${projectId}/edit`)}
              sx={{ ml: 1 }}
            >
            </Button>
          )}
        </Box>
        <Box display="flex" gap={2}>
          {selectedDocument && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={handleCancelDocument}
              disabled={!canCancelSelectedDocument}
            >
              {selectedDocument.is_cancelled ? 'Document Cancelled' : 'Cancel Document'}
            </Button>
          )}
          {activeTab === 'documents' && showDocumentCreateOrEditButton && (
            <Tooltip title={isProjectClosed ? 'Project is closed. Document creation/editing is locked.' : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={selectedDocument && canEditSelectedDocument ? <EditIcon /> : <CloudUploadIcon />}
                  onClick={handleSmartDocumentButtonClick}
                  disabled={isProjectClosed}
                  sx={{
                    minWidth: '160px',
                    bgcolor: NAUTILUS.sand,
                    color: NAUTILUS.deepOcean,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: alpha(NAUTILUS.sand, 0.85),
                    },
                  }}
                >
                  {selectedDocument && canEditSelectedDocument ? 'Edit Document' : 'Create Document'}
                </Button>
              </span>
            </Tooltip>
          )}

          {projectTemplates.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => setTemplateDialogOpen(true)}
              sx={{ minWidth: '180px' }}
            >
              Download Templates
            </Button>
          )}

          {activeTab === 'documents' && canCreateDocuments && (
            <Tooltip title={isProjectClosed ? 'Project is closed. Bulk document creation is locked.' : ''}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setBulkUploadDialogOpen(true)}
                  sx={{ minWidth: '180px' }}
                  disabled={isProjectClosed}
                >
                  Bulk Documents Creation
                </Button>
              </span>
            </Tooltip>
          )}

          {isProjectClosed && projectId && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => setCompletionLetterDialogOpen(true)}
              sx={{ minWidth: '220px' }}
            >
              Generate Completion Letter
            </Button>
          )}

          {isProjectClosed && projectId && (
            <Tooltip
              title={
                feedbackCount > 0
                  ? 'Feedback already entered. Open the Feedback tab to view or edit it.'
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={<RateReviewIcon />}
                  onClick={() => setFeedbackDialogOpen(true)}
                  disabled={feedbackCount > 0}
                  sx={{ minWidth: '160px' }}
                >
                  Enter feedback
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="document view tabs">
          <Tab label="Project Documents" value="documents" />
          {canAccessCfos && <Tab label="CFOS" value="cfos" />}
          <Tab label="Transmittals" value="transmittals" />
          {feedbackCount > 0 && <Tab label="Feedback" value="feedback" />}
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isProjectClosed && (
        <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
          This project is closed. Document creation/editing and bulk creation are locked.
        </Alert>
      )}

      <Box ref={tablePanelRef} sx={tabPanelSx(activeTab === 'documents')} role="tabpanel">
        <MDRTableAgGrid
          data={documents}
          projects={projects}
          revisionNames={revisionNames}
          parties={parties}
          onUpdateDate={async (docId, revId, date) => {
            await projectsService.updateMDRRevision(docId, revId, date);
            loadDocuments();
          }}

          onAddRevision={async (docId, data) => {
            if (isProjectClosed) {
              const message = 'This project is closed. Revision creation is locked.';
              setError(message);
              throw new Error(message);
            }
            const existingRevisions = await projectsService.getDocumentRevisions(Number(projectId), docId);
            const payload = { ...data };
            if (payload.workflow && Array.isArray(payload.workflow)) {
              const checkers: any[] = [];
              const approvers: any[] = [];
              let authorPartyId: number | undefined = undefined;

              payload.workflow.forEach((step: WorkflowStep, index: number) => {
                if (step.role === 'checker' && step.party_id) {
                  checkers.push({ party_id: step.party_id, step_order: index + 1 });
                } else if (step.role === 'approver' && step.party_id) {
                  approvers.push({ party_id: step.party_id, step_order: index + 1 });
                } else if (step.role === 'author' && step.party_id) {
                  authorPartyId = step.party_id;
                }
              });

              payload.checkers = checkers;
              payload.approvers = approvers;
              payload.author_party_id = authorPartyId;
              delete payload.workflow;
            }

            const wouldStartWorkflow = Boolean(
              payload.author_party_id ||
              (payload.checkers && payload.checkers.length > 0) ||
              (payload.approvers && payload.approvers.length > 0)
            );
            if (wouldStartWorkflow && hasStartedRevision(existingRevisions)) {
              setError(WORKFLOW_ASSIGNMENT_CONFLICT_ERROR);
              throw new Error(WORKFLOW_ASSIGNMENT_CONFLICT_ERROR);
            }

            try {
              const result = await projectsService.createDocumentRevision(
                Number(projectId),
                docId,
                payload as CreateDocumentRevisionForm
              );
              loadDocuments();
              return { revision_id: result.revision_id };
            } catch (err: any) {
              const message =
                err?.response?.data?.error ||
                err?.message ||
                'Failed to create revision';
              setError(message);
              throw new Error(message);
            }
          }}
          onDownloadLatest={handleDownloadLatest}
          onRowClick={(data) => {
            if (!data) {
              setSelectedDocument(null);
              return;
            }
            setSelectedDocument((prev) => {
              if (prev?.document_id === data.document_id) {
                return null;
              }
              return data;
            });
          }}
          onRowDoubleClick={(data) => navigate(`/projects/${projectId}/documents/${data.document_id}/revisions`)}
          showProjectFilter={false}
          viewMode="simple"
          revisionFiles={revisionFiles}
          onExpandRevision={handleExpandRevision}
          onDownloadFile={handleDownloadFile}
        />
      </Box>

      {canAccessCfos && (
        <Box
          sx={tabPanelSx(activeTab === 'cfos')}
          role="tabpanel"
        >
          {activeTab === 'cfos' && (
            <>
              {cfosLoadError && <Alert severity="error" sx={{ mb: 2 }}>{cfosLoadError}</Alert>}
              {!cfosLoadError && (!cfosUser || !cfosSandboxId) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {cfosUser && cfosSandboxId && (
                <CfosWorkspace
                  user={cfosUser}
                  sandboxId={cfosSandboxId}
                  compact
                />
              )}
            </>
          )}
        </Box>
      )}

      <Box sx={tabPanelSx(activeTab === 'transmittals')} role="tabpanel">
        {activeTab === 'transmittals' && projectId && <TransmittalsTable projectId={Number(projectId)} />}
      </Box>

      {feedbackCount > 0 && (
        <Box sx={tabPanelSx(activeTab === 'feedback')} role="tabpanel">
          {activeTab === 'feedback' && projectId && (
            <ProjectCompletionFeedbackPanel
              projectId={Number(projectId)}
              onFeedbackCountChange={(count) => setFeedbackCount(count)}
            />
          )}
        </Box>
      )}

      {projectId && (
        <UnifiedDocumentDialog
          open={documentDialogOpen}
          onClose={() => setDocumentDialogOpen(false)}
          mode={selectedDocument && canEditSelectedDocument ? 'edit' : 'create'}
          document={selectedDocument && canEditSelectedDocument ? selectedDocument : null}
          projectId={Number(projectId)}
          workPackages={workPackages}
          workUnits={workUnits}
          documentTypes={documentTypes}
          disciplines={disciplines}
          projectUsers={projectUsers}
          currentProject={currentProject}
          parties={parties}
          revisionNames={revisionNames}
          onLoadWorkUnits={loadDropdownData}
          onSuccess={() => {
            setDocumentDialogOpen(false);
            setError(null);
            loadDocuments();
          }}
          onError={(message) => setError(message)}
        />
      )}

      {/* Bulk Upload Document Dialog */}
      <Dialog
        open={bulkUploadDialogOpen}
        onClose={() => setBulkUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${panelBorder}`,
            py: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              Bulk Upload Documents
            </Typography>
            <IconButton onClick={() => setBulkUploadDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Upload an Excel file with two sheets named <strong>documents</strong> and <strong>revisions</strong>.
            The downloaded template includes a START HERE sheet with the rules, plus copy-paste examples.
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={() => projectsService.downloadTemplate()}
              sx={{ textTransform: 'none', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}
            >
              Download Excel Template
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<DescriptionIcon />}
                sx={{ height: '56px', borderColor: panelBorder }}
              >
                {bulkUploadForm.template_file ? bulkUploadForm.template_file.name : 'Select Excel Template'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setBulkUploadForm({ ...bulkUploadForm, template_file: file });
                  }}
                />
              </Button>
            </Grid>
          </Grid>

          {bulkUploadResults && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={bulkUploadResults.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setBulkUploadResults(null)}>
                {bulkUploadResults.message}
              </Alert>
              {bulkUploadResults.results.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Successfully Created:</Typography>
                  {bulkUploadResults.results.map((r, i) => (
                    <Typography key={i} variant="body2">• {r.nd_number}: {r.title}</Typography>
                  ))}
                </Box>
              )}
              {bulkUploadResults.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Errors:</Typography>
                  {bulkUploadResults.errors.map((e, i) => (
                    <Typography key={i} variant="body2" color="error">• {e}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
          <Button
            onClick={() => {
              setBulkUploadDialogOpen(false);
              setBulkUploadResults(null);
            }}
            sx={{ color: 'text.secondary' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkUpload}
            disabled={bulkUploading || !bulkUploadForm.template_file}
            sx={{
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              fontWeight: 600,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
              },
              '&:disabled': {
                bgcolor: alpha(NAUTILUS.sand, 0.3),
              },
            }}
          >
            {bulkUploading ? <CircularProgress size={20} sx={{ color: NAUTILUS.deepOcean }} /> : 'Upload Documents'}
          </Button>
        </DialogActions>
      </Dialog>

      <DownloadSelectionDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        files={templateDownloadItems}
        onView={handleViewTemplate}
        onDownload={handleDownloadTemplate}
        onDownloadAll={handleDownloadAllTemplates}
        isDownloadingAll={downloadingAllTemplates}
      />

      <DownloadSelectionDialog
        open={revisionDownloadDialogOpen}
        onClose={() => setRevisionDownloadDialogOpen(false)}
        files={revisionDownloadFiles}
        loading={revisionDownloadLoading}
        onView={handleViewRevisionVersionFile}
        onDownload={handleDownloadRevisionVersionFile}
        onDownloadAll={handleDownloadRevisionVersionFilesAsZip}
        isDownloadingAll={revisionZipDownloading}
      />

      {projectId && (
        <CompletionLetterDialog
          open={completionLetterDialogOpen}
          onClose={() => setCompletionLetterDialogOpen(false)}
          projectId={Number(projectId)}
        />
      )}

      {projectId && (
        <ProjectCompletionFeedbackDialog
          open={feedbackDialogOpen}
          onClose={() => {
            setFeedbackDialogOpen(false);
            if (isProjectClosed) loadFeedbackCount();
          }}
          projectId={Number(projectId)}
          project={currentProject}
          alreadyEntered={feedbackCount > 0}
        />
      )}

    </Box>
  );
};

export default DocumentsPage;